import { useCallback, useReducer, useRef, useState } from 'react'

import { Alert, Box, LinearProgress, Stack } from '@mui/material'
import { keyframes, styled } from '@mui/material/styles'
import { type BreadcrumbItem, FileDropArea, FileUploadCard, InfoBox, Modal, TinyButton, tokens, TOOLBAR_HEIGHT } from 'ComponentLibrary'
import {
    ArrowsClockwiseIcon,
    ChatCircleDotsIcon,
    FastForwardIcon,
    FileArchiveIcon,
    MagnifyingGlassIcon,
    SelectionSlashIcon,
    SparkleIcon,
    UploadSimpleIcon,
    WarningIcon,
} from '@phosphor-icons/react'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

import { preprocessPdf } from 'ComponentLibrary/src/playground/shared/pdf/pdf-preprocessor'
import { loadSamplePdfFile } from 'ComponentLibrary/src/playground/shared/sample-pdf'
import { deriveValXmlAssignmentsFromKpis, mergeExtractedKpis, toUserErrorMessage } from './utils/response-processor'
import { buildValXml, deriveValXmlFilename } from './utils/valxml-builder'
import { ContentColumn, ContentRow, PdfContentColumn } from 'ComponentLibrary/src/playground/tools/ValXmlExtractor/components/styled'
import { EmptyPdfStateCard, EmptyPdfStateContent, EmptyPdfStateIllustration } from 'ComponentLibrary/src/playground/shared/pdf/styled'
import type { KpiRow } from './types'
import { DEFAULT_KPI_SECTION_COPY, KpiSection, type KpiSectionCopy } from './components/KpiSection'
import {
    DEFAULT_PDF_PREVIEW_COPY,
    PdfPreviewSection,
    type PdfPreviewSectionCopy,
} from 'ComponentLibrary/src/playground/shared/pdf/PdfPreviewSection'
import { Typography } from 'ComponentLibrary/src/piazza'
import { TextButtonNew } from 'ComponentLibrary/src/piazza/buttons'
import { MANUAL_ENTRY_CONFIDENCE, MAX_VALXML_PDF_FILE_SIZE_BYTES, REQUIRED_IMPORT_DEFAULT_NOTE } from './constants'
import { initialState, reducer } from 'ComponentLibrary/src/playground/tools/ValXmlExtractor/extractor-state'
import { applySecondaryCheckDisagreements, DEFAULT_SECONDARY_CHECK_COPY, type SecondaryCheckCopy } from './utils/secondary-check'
import { readFileAsBase64 } from 'ComponentLibrary/src/playground/shared/upload/read-file'
import {
    DEFAULT_PLAYGROUND_TOOL_HEADER_COPY,
    PlaygroundToolHeader,
    type PlaygroundToolHeaderCopy,
} from 'ComponentLibrary/src/playground/shared/layout/PlaygroundToolHeader'
import { PlaygroundToolPageContainer as RootContainer } from 'ComponentLibrary/src/playground/shared/layout/PlaygroundToolPageContainer'
import { FeedbackTool, type FeedbackToolProps } from 'ComponentLibrary/src/molecules/FeedbackTool/FeedbackTool'
import { DEFAULT_FILE_UPLOAD_COPY, type FileUploadCopy } from 'ComponentLibrary/src/playground/shared/upload/file-upload-copy'
import {
    buildValXmlExtractCommand,
    buildValXmlReviewCommand,
    toKpiRows,
    toReviewDisagreements,
    type ValXmlExtractionWorkflowResult,
    type ValXmlReviewWorkflowResult,
    type ValXmlWorkflowInvoker,
    type ValXmlWorkflowKpi,
    type ValXmlWorkflowPdfFile,
    type ValXmlWorkflowProgressSnapshot,
    type ValXmlWorkflowWarning,
} from './utils/workflow'

export type { ValXmlWorkflowInvoker, ValXmlWorkflowProgressSnapshot, ValXmlWorkflowWarning } from './utils/workflow'

const ellipsisAnimation = keyframes`
    0% { content: '.'; }
    33% { content: '..'; }
    66% { content: '...'; }
    100% { content: '...'; }
`

const LoadingEllipsis = styled('span')(() => ({
    '&::after': {
        content: '""',
        display: 'inline-block',
        minWidth: '1.5em',
        textAlign: 'left',
        marginLeft: '4px',
        animation: `${ellipsisAnimation} 1.2s steps(1, end) infinite`,
    },
}))

const getValXmlInfoModalIcon = (index: number) => {
    switch (index) {
        case 0:
            return <SparkleIcon />
        case 1:
            return <FileArchiveIcon />
        case 2:
            return <SelectionSlashIcon />
        case 3:
            return <MagnifyingGlassIcon />
        case 4:
            return <ChatCircleDotsIcon />
        default:
            return <SparkleIcon />
    }
}

export type ValXmlExtractorCopy = {
    toolHeader: PlaygroundToolHeaderCopy
    pdfPreview: PdfPreviewSectionCopy
    upload: FileUploadCopy
    kpiSection: KpiSectionCopy
    secondaryCheck: SecondaryCheckCopy
    title: string
    infoText: string
    infoModalItems: string[]
    emptyDescription: string
    aiResponse: {
        language: string
        marketRentTerm: string
        requiredImportDefaultNote: string
    }
    progress: {
        defaultExtraction: string
        extractingSection: (section: string) => string
        sectionLabel: (label: string) => string
    }
    errors: {
        missingReviewData: string
        missingReviewKpis: string
        noReadableText: string
        pdfProcessing: string
        pdfProviderFit: string
        samplePdf: string
        valXmlBuild: string
        authExpired: string
        forbidden: string
        endpointMissing: string
        rateLimit: string
        server: string
        missingToken: string
        responseMissingJson: string
        responseInvalidJson: string
        responseInvalidFormat: string
        unknown: string
        genericAiError: (code?: number | string, bodyMessage?: string) => string
    }
}

export const DEFAULT_VAL_XML_EXTRACTOR_COPY: ValXmlExtractorCopy = {
    toolHeader: DEFAULT_PLAYGROUND_TOOL_HEADER_COPY,
    pdfPreview: DEFAULT_PDF_PREVIEW_COPY,
    upload: {
        ...DEFAULT_FILE_UPLOAD_COPY,
        fileSizeErrorMessage: 'Die Datei ist größer als 20 MB. Bitte PDF komprimieren und erneut versuchen.',
    },
    kpiSection: DEFAULT_KPI_SECTION_COPY,
    secondaryCheck: DEFAULT_SECONDARY_CHECK_COPY,
    title: 'PDF zu ValXML',
    infoText: 'Lassen Sie die wichtigsten Daten automatisiert aus Ihrem Gutachten lesen und als ValXML-Datei exportieren.',
    infoModalItems: [
        'Unser Tool automatisiert das Auslesen und Exportieren der Daten aus Gutachten-PDFs mittels KI und stellt die Daten als ValXML Download zur Verfügung.',
        'ValXML ist ein standardisiertes Dateiformat, das vom Bundesverband der Immobilien-Investment-Sachverständigen (BIIS) für den Austausch von bewertungsbezogenen Daten entwickelt wurde.',
        'Nicht alle Bewertungsmethoden können im ValXML-Format dargestellt werden.',
        'KI kann Fehler machen, bitte überprüfen Sie die Daten bevor Sie diese verwenden. Sie können im Tool manuell Daten hinzufügen, ändern oder entfernen.',
        'Sie benötigen die Daten in einem anderen Format? Schreiben Sie uns Feedback!',
    ],
    emptyDescription: 'Wählen Sie ein Gutachten aus, um Kennzahlen zu extrahieren und als ValXML zu exportieren.',
    aiResponse: {
        language: 'Deutsch (de-DE)',
        marketRentTerm: 'Marktmiete',
        requiredImportDefaultNote: REQUIRED_IMPORT_DEFAULT_NOTE,
    },
    progress: {
        defaultExtraction: 'Kennzahlen werden extrahiert',
        extractingSection: (section) => `${section} werden extrahiert`,
        sectionLabel: (label) => label,
    },
    errors: {
        missingReviewData: 'Es liegt noch keine KI-Auswertung zum Prüfen vor.',
        missingReviewKpis: 'Es wurden noch keine KPIs extrahiert, die geprüft werden könnten.',
        noReadableText: 'Im Dokument wurden keine lesbaren Texte gefunden.',
        pdfProcessing: 'Die PDF-Datei konnte nicht verarbeitet werden. Prüfe bitte das Dokument und versuche es erneut.',
        pdfProviderFit:
            'Die PDF-Datei konnte nicht für die KI-Verarbeitung vorbereitet werden. Bitte komprimieren Sie die Datei und versuchen Sie es erneut.',
        samplePdf: 'Das Mustergutachten konnte nicht geladen werden.',
        valXmlBuild: 'Die ValXML konnte nicht erstellt werden. Bitte ergänze fehlende ValXML-Pfade und versuche es erneut.',
        authExpired: 'Nicht angemeldet oder Sitzung abgelaufen. Bitte erneut anmelden.',
        forbidden: 'Zugriff verweigert. Dir fehlen Berechtigungen für die KI-Funktion.',
        endpointMissing: 'KI-Endpunkt nicht gefunden. Prüfe VITE_BACKEND_ROUTE und Backend-Verfügbarkeit.',
        rateLimit: 'Limit erreicht. Bitte später erneut versuchen.',
        server: 'Serverfehler bei der KI. Bitte später erneut versuchen.',
        missingToken: 'Nicht angemeldet. Bitte melde dich an und versuche es erneut.',
        responseMissingJson: 'Die KI-Antwort enthielt kein JSON.',
        responseInvalidJson: 'Die KI-Antwort war kein gültiges JSON.',
        responseInvalidFormat: 'Die Extraktionsantwort war nicht im erwarteten JSON-Format.',
        unknown: 'Unbekannter Fehler beim Abrufen der KI-Antwort.',
        genericAiError: (code, bodyMessage) =>
            bodyMessage ? `Fehler (${code ?? 'Unbekannt'}): ${bodyMessage}` : `Fehler (${code ?? 'Unbekannt'}) beim KI-Aufruf.`,
    },
}

const buildWorkflowPdf = async (file: File): Promise<ValXmlWorkflowPdfFile> => ({
    name: file.name,
    source: {
        sourceType: 'BYTE_CONTENT',
        byteContent: {
            mediaType: 'application/pdf',
            data: await readFileAsBase64(file),
        },
    },
})

const toWorkflowErrorMessage = (error: unknown, copy: ValXmlExtractorCopy['errors']) =>
    error instanceof Error && error.message === 'pdf_provider_fit_failed' ? copy.pdfProviderFit : toUserErrorMessage(error, copy)

const getLocalizedText = (text: { de: string; en: string }, outputLanguage: string) =>
    outputLanguage.toLowerCase().includes('en-') ? text.en : text.de

const isFinishedProgressStatus = (status: ValXmlWorkflowProgressSnapshot['steps'][number]['status']) =>
    status === 'completed' || status === 'not_found' || status === 'skipped'

type ValXmlExtractorProps = {
    invokeWorkflow: ValXmlWorkflowInvoker
    feedbackButton?: FeedbackToolProps
    onValXmlDownloaded?: () => void
    breadcrumbRoot?: BreadcrumbItem
    onDataPrivacyModalOpen?: () => void
    onInfoModalOpen?: () => void
    onSamplePdfUsed?: () => void
    copy?: ValXmlExtractorCopy
}

export const ValXmlExtractor = ({
    invokeWorkflow,
    breadcrumbRoot,
    feedbackButton,
    onValXmlDownloaded,
    onDataPrivacyModalOpen,
    onInfoModalOpen,
    onSamplePdfUsed,
    copy = DEFAULT_VAL_XML_EXTRACTOR_COPY,
}: ValXmlExtractorProps) => {
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
    const [modalSelectedFile, setModalSelectedFile] = useState<File | null>(null)
    const [isEmptyStateDropOver, setIsEmptyStateDropOver] = useState(false)
    const [state, dispatch] = useReducer(reducer, initialState)
    const { uploadedFile, pdfUrl, kpiRows, processingError, isPreprocessing, pageTexts, focusHighlight, reviewState, lastReviewSummary } =
        state
    const [isExtracting, setIsExtracting] = useState(false)
    const [hasCompletedExtraction, setHasCompletedExtraction] = useState(false)
    const [workflowProgress, setWorkflowProgress] = useState<ValXmlWorkflowProgressSnapshot | null>(null)
    const [builderWarnings, setBuilderWarnings] = useState<string[]>([])
    const latestExtractionRowsRef = useRef<KpiRow[]>([])

    const handleReviewSummaryShown = useCallback(() => {
        dispatch({ type: 'SET_LAST_REVIEW_SUMMARY', payload: null })
    }, [dispatch])

    const isExtractionInFlight = isExtracting
    const reviewMutationInFlight = reviewState.status === 'pending'
    const isBusy = isPreprocessing || isExtracting || reviewMutationInFlight
    const canRunReview = Boolean(hasCompletedExtraction && uploadedFile && kpiRows.length > 0)

    const reconcileWorkflowWarnings = useCallback((warnings: ValXmlWorkflowWarning[]) => {
        setWorkflowProgress((current) => {
            if (current) {
                return { ...current, warnings }
            }
            if (warnings.length === 0) {
                return null
            }
            return { version: 1, sequence: 0, steps: [], partial_results: [], warnings }
        })
    }, [])

    const handleRunReview = useCallback(async () => {
        if (!uploadedFile) {
            dispatch({
                type: 'SET_REVIEW_STATE',
                payload: {
                    status: 'error',
                    disagreements: [],
                    errorMessage: copy.errors.missingReviewData,
                },
            })
            return
        }
        if (reviewMutationInFlight) {
            return
        }
        if (kpiRows.length === 0) {
            dispatch({
                type: 'SET_REVIEW_STATE',
                payload: {
                    status: 'error',
                    disagreements: [],
                    errorMessage: copy.errors.missingReviewKpis,
                },
            })
            return
        }

        dispatch({ type: 'SET_REVIEW_STATE', payload: { status: 'pending', disagreements: [] } })

        const clearedRows = kpiRows.map((row) => ({
            ...row,
            needsReview: false,
            reviewNotes: undefined,
            lastReviewRound: undefined,
            reviewDisagreed: false,
            proposedValue: null,
            proposedMetadata: null,
        }))
        dispatch({ type: 'SET_KPI_ROWS', payload: clearedRows })

        try {
            const response = await invokeWorkflow({
                phase: 'review',
                prompt: buildValXmlReviewCommand({
                    outputLanguage: copy.aiResponse.language,
                    marketRentTerm: copy.aiResponse.marketRentTerm,
                    currentKpis: clearedRows,
                }),
                files: [await buildWorkflowPdf(uploadedFile)],
                onProgress: setWorkflowProgress,
            })
            const workflowResult = JSON.parse(response.response) as ValXmlReviewWorkflowResult
            const result = applySecondaryCheckDisagreements({
                disagreements: toReviewDisagreements(workflowResult.disagreements),
                currentRows: clearedRows,
                copy: copy.secondaryCheck,
            })

            dispatch({ type: 'SET_KPI_ROWS', payload: result.updatedRows })
            dispatch({ type: 'SET_LAST_REVIEW_SUMMARY', payload: result.summaryMessage })
            dispatch({
                type: 'SET_REVIEW_STATE',
                payload: { status: 'success', disagreements: result.disagreements, errorMessage: undefined },
            })
            reconcileWorkflowWarnings(workflowResult.warnings)
        } catch (error) {
            dispatch({
                type: 'SET_REVIEW_STATE',
                payload: {
                    status: 'error',
                    disagreements: [],
                    errorMessage: toWorkflowErrorMessage(error, copy.errors),
                },
            })
        }
    }, [
        copy.aiResponse.language,
        copy.aiResponse.marketRentTerm,
        copy.errors,
        copy.secondaryCheck,
        dispatch,
        invokeWorkflow,
        kpiRows,
        reconcileWorkflowWarnings,
        reviewMutationInFlight,
        uploadedFile,
    ])

    const handleFileSelection = useCallback(
        async (files: File[]) => {
            const [file] = files
            if (!file) {
                return
            }

            if (file.size > MAX_VALXML_PDF_FILE_SIZE_BYTES) {
                setWorkflowProgress(null)
                dispatch({ type: 'SET_PROCESSING_ERROR', payload: copy.upload.fileSizeErrorMessage })
                return
            }

            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl)
            }

            latestExtractionRowsRef.current = []
            setWorkflowProgress(null)
            setIsExtracting(false)
            setHasCompletedExtraction(false)
            setBuilderWarnings([])
            dispatch({ type: 'SET_PROCESSING_ERROR', payload: null })
            dispatch({ type: 'SET_UPLOADED_FILE', payload: null })
            dispatch({ type: 'SET_PDF_URL', payload: null })
            dispatch({ type: 'START_PREPROCESSING' })
            dispatch({ type: 'SET_KPI_ROWS', payload: [] })
            dispatch({ type: 'SET_PAGE_TEXTS', payload: [] })
            dispatch({ type: 'SET_FOCUS_HIGHLIGHT', payload: null })
            dispatch({ type: 'SET_REVIEW_STATE', payload: { status: 'idle', disagreements: [] } })
            dispatch({ type: 'SET_LAST_REVIEW_SUMMARY', payload: null })

            let processed: Awaited<ReturnType<typeof preprocessPdf>> | null = null

            try {
                const [nextProcessed, workflowPdf] = await Promise.all([preprocessPdf(file), buildWorkflowPdf(file)])
                processed = nextProcessed
                if (!processed.text) {
                    dispatch({ type: 'SET_PROCESSING_ERROR', payload: copy.errors.noReadableText })
                    return
                }

                dispatch({ type: 'SET_UPLOADED_FILE', payload: file })
                dispatch({ type: 'SET_PAGE_TEXTS', payload: processed.pageTexts })
                dispatch({ type: 'SET_PDF_URL', payload: URL.createObjectURL(file) })
                setIsExtracting(true)

                const response = await invokeWorkflow({
                    phase: 'extract',
                    prompt: buildValXmlExtractCommand({
                        outputLanguage: copy.aiResponse.language,
                        marketRentTerm: copy.aiResponse.marketRentTerm,
                        requiredImportDefaultNote: copy.aiResponse.requiredImportDefaultNote,
                    }),
                    files: [workflowPdf],
                    onProgress: setWorkflowProgress,
                    onPartialResult: (partialResult) => {
                        if (partialResult.step_id !== 'kpis') {
                            return
                        }
                        const rows = toKpiRows(partialResult.value as ValXmlWorkflowKpi[])
                        latestExtractionRowsRef.current = rows
                        dispatch({ type: 'SET_KPI_ROWS', payload: rows })
                    },
                })
                const result = JSON.parse(response.response) as ValXmlExtractionWorkflowResult
                const terminalRows = toKpiRows(result.kpis)
                const reconciledRows = mergeExtractedKpis(terminalRows, latestExtractionRowsRef.current)
                latestExtractionRowsRef.current = reconciledRows
                dispatch({ type: 'SET_KPI_ROWS', payload: reconciledRows })

                setHasCompletedExtraction(true)
                dispatch({ type: 'SET_PROCESSING_ERROR', payload: null })
                dispatch({ type: 'SET_REVIEW_STATE', payload: { status: 'idle', disagreements: [] } })
                dispatch({ type: 'SET_LAST_REVIEW_SUMMARY', payload: null })
                reconcileWorkflowWarnings(result.warnings)
            } catch (error) {
                setHasCompletedExtraction(false)
                if (!processed) {
                    dispatch({
                        type: 'SET_PROCESSING_ERROR',
                        payload: copy.errors.pdfProcessing,
                    })
                } else {
                    dispatch({ type: 'SET_PROCESSING_ERROR', payload: toWorkflowErrorMessage(error, copy.errors) })
                }
            } finally {
                setIsExtracting(false)
                dispatch({ type: 'STOP_PREPROCESSING' })
            }
        },
        [
            copy.aiResponse.language,
            copy.aiResponse.marketRentTerm,
            copy.aiResponse.requiredImportDefaultNote,
            copy.errors,
            copy.upload.fileSizeErrorMessage,
            dispatch,
            invokeWorkflow,
            pdfUrl,
            reconcileWorkflowWarnings,
        ]
    )

    const handleOpenUploadModal = useCallback(() => {
        if (isBusy) {
            return
        }
        setModalSelectedFile(null)
        setIsUploadModalOpen(true)
    }, [isBusy])

    const handleCloseUploadModal = useCallback(() => {
        setModalSelectedFile(null)
        setIsUploadModalOpen(false)
    }, [])

    const handleUploadModalFileSelect = useCallback(
        (file?: File) => {
            setModalSelectedFile(file ?? null)
            dispatch({ type: 'SET_PROCESSING_ERROR', payload: null })
        },
        [dispatch]
    )

    const handleConfirmUploadModal = useCallback(() => {
        if (!modalSelectedFile) {
            return
        }

        const fileToUpload = modalSelectedFile
        setModalSelectedFile(null)
        setIsUploadModalOpen(false)
        void handleFileSelection([fileToUpload])
    }, [handleFileSelection, modalSelectedFile])

    const handleEmptyStateDrop = useCallback(
        (_areaId?: string, files?: File[]) => {
            if (isBusy || !files?.length) {
                return
            }
            void handleFileSelection(files)
        },
        [handleFileSelection, isBusy]
    )

    const handleUseSamplePdf = useCallback(async () => {
        if (isBusy) {
            return
        }

        try {
            onSamplePdfUsed?.()
            dispatch({ type: 'SET_PROCESSING_ERROR', payload: null })
            const samplePdf = await loadSamplePdfFile()
            void handleFileSelection([samplePdf])
        } catch {
            dispatch({ type: 'SET_PROCESSING_ERROR', payload: copy.errors.samplePdf })
        }
    }, [copy.errors.samplePdf, dispatch, handleFileSelection, isBusy, onSamplePdfUsed])

    const handleRetryExtraction = useCallback(() => {
        if (!uploadedFile || isBusy) {
            return
        }
        void handleFileSelection([uploadedFile])
    }, [handleFileSelection, isBusy, uploadedFile])

    const handleKpiRowClick = useCallback(
        (row: KpiRow) => {
            const source = row.sourceText?.trim() ?? ''
            if (!source) {
                dispatch({ type: 'SET_FOCUS_HIGHLIGHT', payload: null })
            } else if (row.page && Number.isFinite(row.page)) {
                dispatch({ type: 'SET_FOCUS_HIGHLIGHT', payload: { term: source, page: row.page } })
            } else {
                dispatch({ type: 'SET_FOCUS_HIGHLIGHT', payload: { term: source } })
            }
        },
        [dispatch]
    )

    const handleValXmlDownload = useCallback(() => {
        if (typeof document === 'undefined') {
            return
        }

        const assignments = deriveValXmlAssignmentsFromKpis(kpiRows)
        const { xml, warnings } = buildValXml(assignments)
        setBuilderWarnings(warnings)

        if (!xml) {
            dispatch({
                type: 'SET_PROCESSING_ERROR',
                payload: copy.errors.valXmlBuild,
            })
            return
        }

        dispatch({ type: 'SET_PROCESSING_ERROR', payload: null })

        const filename = deriveValXmlFilename(uploadedFile?.name)
        const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' })
        const blobUrl = URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        URL.revokeObjectURL(blobUrl)
        onValXmlDownloaded?.()
    }, [copy.errors.valXmlBuild, dispatch, uploadedFile, kpiRows, onValXmlDownloaded])

    const handleUpdateKpiRow = useCallback(
        (index: number, updates: Partial<KpiRow>) => {
            const nextRows = kpiRows.map((row, rowIndex) => {
                if (rowIndex !== index) {
                    return row
                }
                const nextRow: KpiRow = { ...row, ...updates }
                const valueExplicitlyUpdated = Object.prototype.hasOwnProperty.call(updates, 'value')
                if (valueExplicitlyUpdated && !Object.prototype.hasOwnProperty.call(updates, 'proposedValue')) {
                    nextRow.proposedValue = null
                    nextRow.proposedMetadata = null
                }
                if (valueExplicitlyUpdated && typeof updates.value === 'string' && updates.value !== row.value) {
                    nextRow.confidence = Object.prototype.hasOwnProperty.call(updates, 'confidence')
                        ? updates.confidence
                        : MANUAL_ENTRY_CONFIDENCE
                }
                if (
                    Object.prototype.hasOwnProperty.call(updates, 'value') ||
                    Object.prototype.hasOwnProperty.call(updates, 'unit') ||
                    Object.prototype.hasOwnProperty.call(updates, 'notes')
                ) {
                    nextRow.lastReviewRound = undefined
                    nextRow.reviewDisagreed = false
                    nextRow.reviewNotes = undefined
                    nextRow.needsReview = false
                }
                return nextRow
            })
            setBuilderWarnings([])
            dispatch({ type: 'SET_KPI_ROWS', payload: nextRows })
            dispatch({ type: 'SET_REVIEW_STATE', payload: { status: 'idle', disagreements: [] } })
        },
        [dispatch, kpiRows]
    )

    const handleAddKpiRow = useCallback(
        (row: KpiRow) => {
            const nextRows = [...kpiRows, { ...row, reviewDisagreed: false, proposedValue: null, proposedMetadata: null }]
            setBuilderWarnings([])
            dispatch({ type: 'SET_KPI_ROWS', payload: nextRows })
            dispatch({ type: 'SET_REVIEW_STATE', payload: { status: 'idle', disagreements: [] } })
        },
        [dispatch, kpiRows]
    )

    const handleRemoveKpiRow = useCallback(
        (index: number) => {
            const nextRows = kpiRows.filter((_, rowIndex) => rowIndex !== index)
            setBuilderWarnings([])
            dispatch({ type: 'SET_KPI_ROWS', payload: nextRows })
            dispatch({ type: 'SET_REVIEW_STATE', payload: { status: 'idle', disagreements: [] } })
        },
        [dispatch, kpiRows]
    )

    const showContentSections = Boolean(uploadedFile || pdfUrl || isPreprocessing || isExtractionInFlight)
    const extractionStep = workflowProgress?.steps.find((step) => step.id === 'kpis')
    const extractionTotalUnits = extractionStep?.total_units ?? 1
    const extractionCompletedUnits =
        extractionStep?.completed_units ?? (extractionStep && isFinishedProgressStatus(extractionStep.status) ? extractionTotalUnits : 0)
    const headerProgress =
        isExtractionInFlight || (processingError && extractionStep)
            ? {
                  value: (extractionCompletedUnits / extractionTotalUnits) * 100,
                  message: extractionStep
                      ? getLocalizedText(extractionStep.description, copy.aiResponse.language)
                      : copy.progress.defaultExtraction,
              }
            : null
    const workflowWarnings =
        workflowProgress?.warnings.map((warning) => ({
            id: warning.warning_type,
            message: getLocalizedText(warning.message, copy.aiResponse.language),
        })) ?? []
    const canRetryExtraction = Boolean(uploadedFile && processingError && !hasCompletedExtraction && !isBusy)

    return (
        <RootContainer
            direction="column"
            sx={{
                maxWidth: 'none',
                margin: 0,
                gap: 0,
                padding: 0,
                paddingBottom: '20px',
                minHeight: `calc(100vh - ${TOOLBAR_HEIGHT} - 1px)`,
                backgroundColor: tokens.ColorBackgroundDark,
            }}
        >
            <PlaygroundToolHeader
                title={copy.title}
                currentLocation="/playground/val-xml-extractor"
                breadcrumbRoot={breadcrumbRoot}
                copy={copy.toolHeader}
                onDataPrivacyModalOpen={onDataPrivacyModalOpen}
                onInfoModalOpen={onInfoModalOpen}
                infoText={copy.infoText}
                infoModalItems={copy.infoModalItems.map((text, index) => ({
                    icon: getValXmlInfoModalIcon(index),
                    text,
                }))}
                actions={
                    showContentSections ? (
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing400}>
                            {headerProgress ? (
                                <Stack
                                    direction="column"
                                    gap={tokens.SizingSpacing075}
                                    sx={{ width: '100%', maxWidth: 360, minWidth: 220, textAlign: 'right' }}
                                >
                                    <Box sx={{ width: '100%' }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={headerProgress.value}
                                            sx={{
                                                borderRadius: tokens.SizingCornerS,
                                                height: 10,
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: tokens.SizingCornerS,
                                                },
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                                        {headerProgress.message}
                                        {isExtractionInFlight ? <LoadingEllipsis aria-hidden /> : null}
                                    </Typography>
                                </Stack>
                            ) : null}
                            <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing150}>
                                {feedbackButton ? <FeedbackTool {...feedbackButton} size="tiny" /> : null}
                                {hasCompletedExtraction ? (
                                    <TinyButton
                                        label={copy.upload.restartAnalysis}
                                        leftIcon={<ArrowsClockwiseIcon />}
                                        onClick={handleRetryExtraction}
                                        disabled={isBusy}
                                    />
                                ) : null}
                                <TinyButton
                                    label={copy.upload.replacePdf}
                                    leftIcon={<ArrowsClockwiseIcon />}
                                    onClick={handleOpenUploadModal}
                                    disabled={isBusy}
                                />
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing150}>
                            {feedbackButton ? <FeedbackTool {...feedbackButton} size="tiny" /> : null}
                            <TextButtonNew
                                variant="primary"
                                label={copy.upload.uploadPdf}
                                leftIcon={<UploadSimpleIcon />}
                                onClick={handleOpenUploadModal}
                                disabled={isBusy}
                            />
                        </Stack>
                    )
                }
            />

            <Stack
                direction="column"
                gap={tokens.SizingSpacing200}
                sx={{
                    paddingTop: tokens.SizingSpacing200,
                    paddingRight: tokens.SizingSpacing200,
                    paddingLeft: tokens.SizingSpacing200,
                    paddingBottom: 0,
                    flex: showContentSections ? '0 0 auto' : '1 1 auto',
                    minHeight: 0,
                }}
            >
                <Modal
                    fullWidth
                    width="600px"
                    showCloseIcon
                    closeIconTooltip={copy.toolHeader.close}
                    isOpen={isUploadModalOpen}
                    onClose={handleCloseUploadModal}
                    title={uploadedFile ? copy.upload.replacePdf : copy.upload.uploadValuationPdf}
                    showLoading={isPreprocessing}
                    footerProps={{
                        primaryButton: {
                            text: uploadedFile ? copy.upload.replacePdf : copy.upload.uploadPdf,
                            icon: uploadedFile ? <ArrowsClockwiseIcon /> : <UploadSimpleIcon />,
                            disabled: !modalSelectedFile,
                            onClick: handleConfirmUploadModal,
                        },
                        secondaryButton: {
                            text: copy.upload.cancel,
                            onClick: handleCloseUploadModal,
                        },
                    }}
                >
                    <Stack width="100%">
                        <FileUploadCard
                            title={copy.upload.valuationPdf}
                            description={copy.upload.dropDescription}
                            file={modalSelectedFile ?? uploadedFile ?? undefined}
                            setFile={handleUploadModalFileSelect}
                            allowedFileTypes={['application/pdf']}
                            disabled={false}
                            errorMessage={processingError ?? undefined}
                            maxFileSizeBytes={MAX_VALXML_PDF_FILE_SIZE_BYTES}
                            fileSizeErrorMessage={copy.upload.fileSizeErrorMessage}
                        />
                    </Stack>
                </Modal>

                {workflowWarnings.map((warning, index) => (
                    <Alert key={`${warning.id}-${index}`} severity="warning">
                        {warning.message}
                    </Alert>
                ))}
                {builderWarnings.map((warning, index) => (
                    <Alert key={`${warning}-${index}`} severity="warning">
                        {warning}
                    </Alert>
                ))}
                {processingError === copy.upload.fileSizeErrorMessage ? (
                    <InfoBox variant="negative" size="small" icon={<WarningIcon />}>
                        {processingError}
                    </InfoBox>
                ) : processingError ? (
                    <Alert
                        severity="error"
                        action={
                            canRetryExtraction ? (
                                <TinyButton
                                    label={copy.upload.restartAnalysis}
                                    leftIcon={<ArrowsClockwiseIcon />}
                                    onClick={handleRetryExtraction}
                                />
                            ) : undefined
                        }
                    >
                        {processingError}
                    </Alert>
                ) : null}
                {reviewState.errorMessage ? <Alert severity="error">{reviewState.errorMessage}</Alert> : null}

                {showContentSections ? (
                    <ContentRow>
                        <ContentColumn direction="column" gap={tokens.SizingSpacing150}>
                            <KpiSection
                                isPending={isExtractionInFlight}
                                kpiRows={kpiRows}
                                onRowClick={handleKpiRowClick}
                                onDownloadValXml={handleValXmlDownload}
                                canDownloadValXml={
                                    hasCompletedExtraction && !isExtractionInFlight && kpiRows.some((row) => Boolean(row.valXmlPath))
                                }
                                onRunReview={handleRunReview}
                                isReviewPending={reviewState.status === 'pending'}
                                canRunReview={canRunReview}
                                onUpdateRow={handleUpdateKpiRow}
                                onAddRow={handleAddKpiRow}
                                onRemoveRow={handleRemoveKpiRow}
                                lastReviewSummary={lastReviewSummary}
                                onReviewSummaryShown={handleReviewSummaryShown}
                                locale={copy.aiResponse.language}
                                copy={copy.kpiSection}
                            />
                        </ContentColumn>

                        <PdfContentColumn direction="column" gap={tokens.SizingSpacing150}>
                            <PdfPreviewSection
                                pdfUrl={pdfUrl}
                                focusHighlight={focusHighlight}
                                pageTexts={pageTexts}
                                onFocusHighlightChange={(fh) => dispatch({ type: 'SET_FOCUS_HIGHLIGHT', payload: fh })}
                                copy={copy.pdfPreview}
                            />
                        </PdfContentColumn>
                    </ContentRow>
                ) : (
                    <Stack direction="column" flex={1} justifyContent="center" alignItems="center">
                        <FileDropArea
                            allowedFileTypes={['application/pdf']}
                            maxFileSizeBytes={MAX_VALXML_PDF_FILE_SIZE_BYTES}
                            fileSizeErrorMessage={copy.upload.fileSizeErrorMessage}
                            onFileValidationError={(message) => dispatch({ type: 'SET_PROCESSING_ERROR', payload: message })}
                            setIsOver={setIsEmptyStateDropOver}
                            onDataDrop={handleEmptyStateDrop}
                            width="532px"
                        >
                            <EmptyPdfStateCard
                                direction="column"
                                justifyContent="center"
                                sx={{
                                    backgroundColor: isEmptyStateDropOver ? tokens.ColorSurfaceHover : tokens.ColorSurfaceDefault,
                                }}
                            >
                                <EmptyPdfStateContent direction="column">
                                    <EmptyPdfStateIllustration height="270px" width="270px" />
                                    <Typography variant="headline2" color={tokens.ColorTextBoldest}>
                                        {copy.upload.emptyTitle}
                                    </Typography>
                                    <Typography variant="textM" color={tokens.ColorTextDefault}>
                                        {copy.emptyDescription}
                                    </Typography>
                                    <Stack direction="row" gap={tokens.SizingSpacing150}>
                                        <TinyButton
                                            onClick={handleOpenUploadModal}
                                            leftIcon={<UploadSimpleIcon />}
                                            label={copy.upload.uploadPdf}
                                            tooltip={copy.upload.uploadTooltip}
                                        />
                                        <TinyButton
                                            onClick={handleUseSamplePdf}
                                            leftIcon={<FastForwardIcon />}
                                            label={copy.upload.sampleButton}
                                            disabled={isBusy}
                                            tooltip={copy.upload.sampleTooltip}
                                        />
                                    </Stack>
                                </EmptyPdfStateContent>
                            </EmptyPdfStateCard>
                        </FileDropArea>
                    </Stack>
                )}
            </Stack>
        </RootContainer>
    )
}
